import { CheckIcon, ClipboardIcon, ArrowLeftIcon } from "@heroicons/react/outline";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { checkMeetingExists } from "../api/meetingLinks";
import {
  buildMeetingShareUrl,
  getRoomFromUrl,
  isValidRoomName,
  sanitizeRoomName,
  setRoomInUrl,
} from "../utils/meeting";

const CRM_HINT =
  "Meeting ID not match. Kindly create meeting from Biz-Meet CRM.";

const CRM_URL =
  process.env.REACT_APP_CRM_MEETING_URL ||
  "https://www.bizconsultancy.iid.org.in/";

export function MeetingDetailsScreen({
  participantName,
  setParticipantName,
  onStartMeeting,
}) {
  const [roomName, setRoomName] = useState("");
  const [roomError, setRoomError] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [mode, setMode] = useState(null);
  const [checking, setChecking] = useState(false);
  const openedFromLink = React.useRef(Boolean(getRoomFromUrl()));

  useEffect(() => {
    const fromUrl = getRoomFromUrl();
    if (fromUrl) {
      setRoomName(fromUrl);
      setMode("join");
    }
  }, []);

  const handleJoinMode = () => {
    setMode("join");
    setRoomError("");
  };

  const handleStart = async () => {
    const sanitized = sanitizeRoomName(roomName);
    if (!isValidRoomName(sanitized)) {
      setRoomError("Enter a valid meeting ID (min 3 characters)");
      return;
    }
    if (participantName.trim().length < 2) {
      toast.error("Please enter your name (min 2 characters)");
      return;
    }

    setChecking(true);
    setRoomError("");
    try {
      const { exists } = await checkMeetingExists(sanitized);
      if (!exists) {
        setRoomError(CRM_HINT);
        toast.error(CRM_HINT);
        return;
      }

      setRoomInUrl(sanitized);
      const hostFlag = sessionStorage.getItem(`biz-host:${sanitized}`) === "1";
      const asHost = hostFlag || (openedFromLink.current && hostFlag);
      onStartMeeting(sanitized, asHost ? "host" : "participant");
    } catch (err) {
      const message = err.message || "Could not verify this meeting ID.";
      setRoomError(message);
      toast.error(message);
    } finally {
      setChecking(false);
    }
  };

  const shareUrl = roomName ? buildMeetingShareUrl(sanitizeRoomName(roomName)) : "";

  return (
    <div className="flex w-full flex-col">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-semibold text-biz-ink sm:text-[1.7rem]">
          Join a meeting
        </h2>
        <p className="mt-1.5 font-sans text-sm text-biz-mute">
          {mode === null
            ? "Use a meeting ID created in Biz-Meet CRM."
            : "Enter the meeting ID from Biz-Meet CRM, add your name, and join."}
        </p>
      </div>

      {!mode && (
        <div className="flex flex-col gap-3 animate-fadeUp">
          <button type="button" className="biz-btn-primary" onClick={handleJoinMode}>
            Join with meeting ID
          </button>
          <a
            href={CRM_URL}
            target="_blank"
            rel="noreferrer"
            className="biz-btn-secondary text-center"
          >
            Create meeting in Biz-Meet CRM
          </a>
        </div>
      )}

      {mode === "join" && (
        <div className="animate-fadeUp">
          {openedFromLink.current && roomName ? (
            <div className="mb-4 flex items-center gap-3 rounded-2xl border border-biz-line bg-biz-soft px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-biz-mute">
                  Meeting ID
                </p>
                <p className="truncate font-sans text-sm font-semibold text-biz-forest">
                  {roomName}
                </p>
              </div>
              <button
                type="button"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl bg-white text-biz-forest shadow-soft transition hover:bg-biz-yellow"
                aria-label="Copy meeting link"
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl || roomName);
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 3000);
                  toast.success("Meeting link copied");
                }}
              >
                {isCopied ? (
                  <CheckIcon className="h-5 w-5 text-biz-forest" />
                ) : (
                  <ClipboardIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          ) : (
            <>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-biz-mute">
                Meeting ID
              </label>
              <input
                value={roomName}
                onChange={(e) => {
                  setRoomName(e.target.value);
                  setRoomError("");
                }}
                placeholder="Paste meeting ID from CRM"
                className="biz-input mb-1"
                autoFocus
              />
            </>
          )}
          {roomError ? (
            <p className="mt-1 text-xs font-medium text-red-600">{roomError}</p>
          ) : null}
        </div>
      )}

      {mode && (
        <div className="mt-4 animate-fadeUpDelay">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-biz-mute">
            Your display name
          </label>
          <input
            value={participantName}
            onChange={(e) => setParticipantName(e.target.value)}
            placeholder="How others will see you"
            className="biz-input"
          />

          <button
            type="button"
            disabled={participantName.trim().length < 2 || checking}
            className="biz-btn-primary mt-5"
            onClick={handleStart}
          >
            {checking ? "Checking meeting…" : "Join meeting"}
          </button>

          <button
            type="button"
            className="mt-3 inline-flex w-full items-center justify-center gap-2 py-2 text-sm font-medium text-biz-mute transition hover:text-biz-forest"
            onClick={() => {
              setMode(null);
              setRoomName("");
              setRoomError("");
            }}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
        </div>
      )}
    </div>
  );
}
